import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery, useClientQuery, useMutation, useSubscription } from 'hooks/apollo';
import FinancialProductRulesEditDetailsServicesTable from './FinancialProductRulesEditDetailsServicesTable';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import {
  FINANCIAL_PRODUCT_RULE_SERVICE_DETAIL_CALC_TYPE_LIST_QUERY,
  FINANCIAL_PRODUCT_RULE_SERVICE_SUPPLIER_LIST_QUERY,
  FINANCIAL_PRODUCT_RULE_SERVICE_TYPE_LIST_QUERY
} from '../../../queries';
import { getInt } from 'utils/functions';
import { useRouteMatch } from 'react-router-dom';
import { useToast } from '@totalsoft/rocket-ui';
import { ADD_UPDATE_FINANCIAL_PRODUCT_RULE_SERVICE, REMOVE_FINANCIAL_PRODUCT_RULE_SERVICE } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_SERVICE_UPDATED, FINANCIAL_PRODUCT_RULE_SERVICE_REMOVED } from '../../../subscriptions';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductRuleServiceValidator } from '../../../validators';
import { emptyString } from 'utils/constants';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const FinancialProductRulesEditDetailsServicesContainer = ({ canWrite, serviceList, isSaved }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);
  const clientQuery = useClientQuery();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [financialProductRuleServiceValidation, validateFinancialProductRuleService] = useDirtyFieldValidation(
    financialProductRuleServiceValidator
  );

  useEffect(() => {
    resetLens(
      serviceList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        elementLocal.modified = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, serviceList]);

  const { data: calcTypeList } = useQuery(FINANCIAL_PRODUCT_RULE_SERVICE_DETAIL_CALC_TYPE_LIST_QUERY);

  const onLoadFinancialProductRuleSupplierList = useCallback(
    async filterText => {
      const data = await clientQuery(FINANCIAL_PRODUCT_RULE_SERVICE_SUPPLIER_LIST_QUERY, {
        variables: { filterText: filterText }
      });
      return data.data.financialProductRuleSupplierList;
    },
    [clientQuery]
  );

  const { data: serviceTypeList } = useQuery(FINANCIAL_PRODUCT_RULE_SERVICE_TYPE_LIST_QUERY);

  useEffect(() => {
    validateFinancialProductRuleService(lens |> get, dirtyInfo);
  }, [lens, dirtyInfo, validateFinancialProductRuleService]);

  const handleAdd = useCallback(() => {
    set(lens)([
      ...(lens |> get),
      {
        financialProductRuleXServiceTypeId: null,
        readOnlyYN: false,
        serviceTypeId: null,
        serviceTypeName: emptyString,
        calcTypeId: null,
        calcTypeName: emptyString,
        supplierId: null,
        supplierName: emptyString,
        percentOrValue: 0,
        periodicity: 0,
        financedYN: false,
        implicit: false,
        mandatory: false,
        isInEditing: true
      }
    ]);
  }, [lens]);

  const handleCloseEdit = orderNo => {
    const lensLocal = [...(lens |> get)];
    if (lensLocal[orderNo].financialProductRuleXServiceTypeId === null) {
      lensLocal.splice(orderNo, 1);
    } else {
      const unmodifiedElement = serviceList.find(
        el => el.financialProductRuleXServiceTypeId === lensLocal[orderNo].financialProductRuleXServiceTypeId
      );
      lensLocal[orderNo] = { ...unmodifiedElement, modified: false, isInEditing: false };
    }
    set(lens)(lensLocal);
  };

  const handleSave = orderNo => {
    if (!validateFinancialProductRuleService(lens |> get)) return;

    const lensLocal = [...(lens |> get)];
    if (lensLocal[orderNo]) lensLocal[orderNo].isSaving = true;

    set(lens)(lensLocal);
    const elementTL = lensLocal[orderNo];
    saveFinancialProductRuleService({
      variables: {
        input: {
          financialProductRuleXServiceTypeId: parseInt(elementTL.financialProductRuleXServiceTypeId),
          financialProductRuleId: ruleId,
          readOnlyYN: elementTL.readOnlyYN,
          serviceTypeId: parseInt(elementTL.serviceTypeId),
          calcTypeId: parseInt(elementTL.calcTypeId),
          supplierId: parseInt(elementTL.supplierId),
          percentOrValue: parseFloat(elementTL.percentOrValue),
          periodicity: parseFloat(elementTL.periodicity),
          financedYN: elementTL.financedYN,
          implicit: elementTL.implicit,
          mandatory: elementTL.mandatory,
          orderNo: parseInt(orderNo)
        }
      }
    });
  };

  const [saveFinancialProductRuleService] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE_SERVICE);

  useSubscription(FINANCIAL_PRODUCT_RULE_SERVICE_UPDATED, {
    onData: ({ data: { data }, client }) => {
      const lensLocal = [...(lens |> get)];
      addToast(t('FinancialProduct.Details.Rules.Details.Services.FinancialProductRuleServiceSaved'), 'success');
      lensLocal.forEach((element, index) => {
        if (index === data.financialProductRuleServiceUpdated.orderNo) {
          element.financialProductRuleXServiceTypeId = data.financialProductRuleServiceUpdated.financialProductRuleXServiceTypeId;
          element.isInEditing = false;
          element.isSaving = false;
          element.modified = false;
        }
      });
      resetLens(lensLocal);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
      client.cache.gc();
    }
  });

  const [deleteFinancialProductRuleService] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE_SERVICE);

  const handleDelete = (financialProductRuleXServiceTypeId, orderNo) => {
    if (window.confirm(t('FinancialProduct.Details.Rules.Details.Services.DeleteFinancialProductRuleService'))) {
      if (financialProductRuleXServiceTypeId) {
        deleteFinancialProductRuleService({
          variables: {
            input: { financialProductRuleXServiceTypeId }
          }
        });
      } else {
        const lensLocal = [...(lens |> get)];
        lensLocal.splice(orderNo, 1);
        set(lens)(lensLocal);
      }
    }
  };

  useSubscription(FINANCIAL_PRODUCT_RULE_SERVICE_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.Services.FinancialProductRuleServiceRemoved'), 'success');
      const lensLocal = [...(lens |> get)];
      const objTL = lensLocal.filter(el => el.financialProductRuleXServiceTypeId !== data.financialProductRuleServiceDeleted.serviceId);
      resetLens(objTL);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return serviceTypeList?.financialProductRuleServiceTypeList && calcTypeList?.financialProductRuleServiceDetailCalcTypeList ? (
    <FinancialProductRulesEditDetailsServicesTable
      canWrite={canWrite}
      serviceList={lens}
      financialProductRuleServiceTypeList={serviceTypeList?.financialProductRuleServiceTypeList}
      financialProductRuleServiceDetailCalcTypeList={calcTypeList?.financialProductRuleServiceDetailCalcTypeList}
      financialProductRuleSupplierList={[]}
      isSaved={isSaved}
      onAdd={handleAdd}
      onCloseEdit={handleCloseEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      financialProductRuleServiceValidation={financialProductRuleServiceValidation}
      onLoadFinancialProductRuleSupplierList={onLoadFinancialProductRuleSupplierList}
    />
  ) : (
    <></>
  );
};

FinancialProductRulesEditDetailsServicesContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  serviceList: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsServicesContainer;
